import { Box, Button, Collapse, Typography } from '@mui/material';
import { useState } from 'react';

import { caloTheme } from '../../assets/themes/calo';
import type { Kitchen } from '../../libs';
import { chefText, outlineButtonSx, quietLinkSx, tabular } from '../chefStyles';
import { describeProductionPlan } from './describeTask';
import { cardSx } from './TaskRow';
import type { HomeTask } from './tasks';
import useCompleteTask from './useCompleteTask';

const { palette } = caloTheme;

type PlanTaskCardProps = { task: HomeTask; kitchen: Kitchen };

const PlanTaskCard = ({ task, kitchen }: PlanTaskCardProps) => {
  const { title, details } = describeProductionPlan(task, kitchen);
  const [open, setOpen] = useState(false);
  const { complete, pending, failed } = useCompleteTask(kitchen, task.id);
  const rationale = task.productionPlan?.rationale;
  const done = task.doneAt !== undefined;
  return (
    <Box sx={{ ...cardSx, display: 'block' }}>
      <Typography
        sx={{
          fontSize: 14,
          lineHeight: '28px',
          fontWeight: 700,
          letterSpacing: '0.5px',
          textTransform: 'uppercase',
          color: palette.neutral800
        }}
      >
        {task.productionPlan?.type ?? 'task'}
      </Typography>
      <Typography sx={{ ...chefText.title, color: done ? palette.neutral800 : palette.neutral900 }}>{title}</Typography>
      <Box sx={{ mt: 0.5 }}>
        {details.map((detail) => (
          <Typography key={detail} sx={{ ...chefText.body, color: palette.neutral800, ...tabular }}>
            {detail}
          </Typography>
        ))}
      </Box>
      {rationale !== undefined && (
        <>
          <Button variant="text" sx={{ ...quietLinkSx, mt: 0.5, ml: -1.5 }} aria-expanded={open} onClick={() => setOpen(!open)}>
            {open ? 'Hide why' : 'Why this task'}
          </Button>
          <Collapse in={open} unmountOnExit>
            <Typography sx={{ ...chefText.body, mt: 0.5, color: palette.neutral800 }}>{rationale}</Typography>
          </Collapse>
        </>
      )}
      {!done && (
        <Box sx={{ mt: 2 }}>
          <Button
            variant="outlined"
            sx={{ ...outlineButtonSx, width: { xs: '100%', md: 'auto' } }}
            disabled={pending}
            onClick={complete}
          >
            Done
          </Button>
          {failed && (
            <Typography role="alert" sx={{ ...chefText.body, mt: 1, color: palette.neutral900 }}>
              Couldn't mark this done. Try again.
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
};

export default PlanTaskCard;
